import { createStore } from "solid-js/store"
import type { CoworkActivityLog } from "@/cowork/api"

const PAGE_SIZE = 50

export function createAuditLogPagination(
  fetchPage: (options: { limit: number; beforeID?: string }) => Promise<CoworkActivityLog[]>,
) {
  const [state, setState] = createStore({
    items: [] as CoworkActivityLog[],
    hasMore: false,
    loading: false,
    error: false,
  })
  let generation = 0
  let disposed = false

  async function load(reset: boolean) {
    if (disposed || (!reset && (state.loading || !state.hasMore))) return
    if (reset) generation++
    const current = generation
    const beforeID = reset ? undefined : state.items.at(-1)?.id
    if (reset) setState({ items: [], hasMore: false })
    setState({ loading: true, error: false })
    try {
      // One lookahead row tells us whether another page exists without a count query.
      const result = await fetchPage({ limit: PAGE_SIZE + 1, beforeID })
      if (current !== generation) return
      const seen = new Set(state.items.map((item) => item.id))
      const page = result.slice(0, PAGE_SIZE).filter((item) => {
        if (seen.has(item.id)) return false
        seen.add(item.id)
        return true
      })
      setState({ items: [...state.items, ...page], hasMore: result.length > PAGE_SIZE })
    } catch {
      if (current === generation) setState("error", true)
    } finally {
      if (current === generation) setState("loading", false)
    }
  }

  return {
    state,
    reset: () => load(true),
    loadMore: () => load(false),
    dispose() {
      disposed = true
      generation++
    },
  }
}
